import type { ResultOf } from '@graphql-typed-document-node/core';

import Skeleton from '@mui/material/Skeleton';

import {
  getFragmentData,
  graphql,
  ResolvableModeEnum,
} from '@e-socna/data-layer';
import { isDefined } from '@e-socna/utils';
import { EMPTY_STRING } from '@e-socna/empty';
import type { CurrencyId, FragmentType, Id } from '@e-socna/data-layer';

import { formatAmount } from '../../utils/format-amount.util';
import { useReferencesContext } from '../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import { formatNumber } from '../../utils/format-number.util';
import { wrapExpression } from '../../utils/handlebars-expression.util';
import { N_A } from '../../constants/labels.constant';
import { checkIfIsId } from '../../utils/check-if-is-id.util';

import { useContentGenerator } from '../DocumentEditor/hooks/useContentGenerator.hook';

import { useResolvableNormalizer } from './hooks/useResolvableNormalizer';
import { PrimaryWrapper as PrimaryWrapperCmp } from './PrimaryWrapper';
import { SecondaryWrapper as SecondaryWrapperCmp } from './SecondaryWrapper';
import type { Props as BaseProps } from './types/props.type';

export const ResolvableAmountCmp_ResolvableAmount_Fragment = graphql(`
  fragment ResolvableAmountCmp_ResolvableAmount_Fragment on ResolvableAmount {
    currencyId {
      __typename
      mode
      resolverId
      value
    }
    value {
      __typename
      mode
      resolverId
      value
    }
  }
`);

export type ResolvableAmountCmp_ResolvableAmount = ResultOf<
  typeof ResolvableAmountCmp_ResolvableAmount_Fragment
>;

interface Props extends BaseProps {
  readonly resolvableAmount: FragmentType<
    typeof ResolvableAmountCmp_ResolvableAmount_Fragment
  >;
}

export const ResolvableAmount = ({
  PrimaryWrapper = PrimaryWrapperCmp,
  resolvableAmount: pResolvableAmount,
  resolvableFieldSettings,
  SecondaryWrapper = SecondaryWrapperCmp,
}: Readonly<Props>) => {
  const resolvableAmount = getFragmentData(
    ResolvableAmountCmp_ResolvableAmount_Fragment,
    pResolvableAmount,
  );

  const { enableDirectGeneration, primaryMode } = resolvableFieldSettings;

  const { currencyId, value } = resolvableAmount;
  const normalizedCurrencyId = useResolvableNormalizer(currencyId);
  const normalizedValue = useResolvableNormalizer(value);

  const { currenciesIdMap } = useReferencesContext();

  const { data: currencyIdData, isSuccess: currencyIdIsSuccess } =
    useContentGenerator({
      enabled:
        normalizedCurrencyId.mode === ResolvableModeEnum.Dynamic &&
        enableDirectGeneration,
      template:
        normalizedCurrencyId.mode === ResolvableModeEnum.Dynamic
          ? wrapExpression(normalizedCurrencyId.resolverCode)
          : EMPTY_STRING,
    });

  const { data: valueData, isSuccess: valueIsSuccess } = useContentGenerator({
    enabled:
      normalizedValue.mode === ResolvableModeEnum.Dynamic &&
      enableDirectGeneration,
    template:
      normalizedValue.mode === ResolvableModeEnum.Dynamic
        ? wrapExpression(normalizedValue.resolverCode)
        : EMPTY_STRING,
  });

  if (
    normalizedCurrencyId.mode === ResolvableModeEnum.Static &&
    normalizedValue.mode === ResolvableModeEnum.Static
  ) {
    if (
      !isDefined(normalizedCurrencyId.merged) ||
      !isDefined(normalizedValue.merged)
    ) {
      return <PrimaryWrapper>{N_A}</PrimaryWrapper>;
    }

    return (
      <PrimaryWrapper>
        {formatAmount(
          {
            value: normalizedValue.merged,
            currencyId: normalizedCurrencyId.merged,
          },
          currenciesIdMap,
        )}
      </PrimaryWrapper>
    );
  }

  const merged =
    typeof normalizedValue.merged === 'number' &&
    checkIfIsId(normalizedCurrencyId.merged)
      ? formatAmount(
          {
            value: normalizedValue.merged,
            currencyId: normalizedCurrencyId.merged,
          },
          currenciesIdMap,
        )
      : `${
          normalizedValue.merged
            ? typeof normalizedValue.merged === 'number'
              ? formatNumber(normalizedValue.merged)
              : wrapExpression(normalizedValue.merged)
            : N_A
        } ${
          normalizedCurrencyId.merged
            ? checkIfIsId(normalizedCurrencyId.merged)
              ? (currenciesIdMap[normalizedCurrencyId.merged as CurrencyId]
                  ?.symbol ?? N_A)
              : wrapExpression(normalizedCurrencyId.merged)
            : N_A
        }`.trim();

  if (!enableDirectGeneration) {
    return <PrimaryWrapper>{merged}</PrimaryWrapper>;
  }

  const isSuccess =
    (normalizedCurrencyId.mode === ResolvableModeEnum.Static ||
      currencyIdIsSuccess) &&
    (normalizedValue.mode === ResolvableModeEnum.Static || valueIsSuccess);

  if (!isSuccess) {
    return (
      <>
        <Skeleton sx={{ display: 'inline-block' }} variant="text" width={80} />{' '}
        <Skeleton sx={{ display: 'inline-block' }} variant="text" width={80} />
      </>
    );
  }

  const dynamicValue =
    normalizedValue.mode === ResolvableModeEnum.Dynamic &&
    isDefined(valueData?.generated)
      ? Number.parseFloat(valueData.generated)
      : normalizedValue.value;
  const dynamicCurrencyId =
    normalizedCurrencyId.mode === ResolvableModeEnum.Dynamic &&
    isDefined(currencyIdData?.generated)
      ? (currencyIdData.generated as Id)
      : normalizedCurrencyId.value;

  if (!isDefined(dynamicValue) || !isDefined(dynamicCurrencyId)) {
    return <PrimaryWrapper>{N_A}</PrimaryWrapper>;
  }

  const dynamic = formatAmount(
    {
      value: dynamicValue,
      currencyId: dynamicCurrencyId,
    },
    currenciesIdMap,
  );

  return primaryMode === ResolvableModeEnum.Static ? (
    <>
      <PrimaryWrapper>{dynamic}</PrimaryWrapper>{' '}
      <SecondaryWrapper>{merged}</SecondaryWrapper>
    </>
  ) : (
    <>
      <PrimaryWrapper>{merged}</PrimaryWrapper>{' '}
      <SecondaryWrapper>{dynamic}</SecondaryWrapper>
    </>
  );
};
